'use client';

import Image from 'next/image';
import { useCallback, useMemo, useState } from 'react';
import { useIsLoading, useUserData } from '@/contexts/LoginContext';
import SupportForm, { FormValues } from '.';
import { useTelegramProductsFindMany, useTelegramSupportContactsCreate } from '@/generated';
import { AfterSupportModal } from '@/modals/AfterSupportModal';
import { useModals } from '@/contexts/ModalContext';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';

type SupportPageProps = {
  productIdForReport?: string;
};

export const SupportPage = ({ productIdForReport }: SupportPageProps) => {
  const contactPhoneHref = 'tel:+972559661843';
  const whatsappHref = 'https://wa.me/972559661843';
  const userData = useUserData();
  const isUserLoading = useIsLoading();
  const [isLoading, setIsLoading] = useState(false);
  const { open, close } = useModals();
  const t = useTranslations('supportPage');
  const pr = userData ? { userData: userData, isLogined: true as const } : { userData, isLogined: false as const };

  const { data: productsData, isLoading: isProductLoading } = useTelegramProductsFindMany(
    { id: [productIdForReport || ''] },
    {
      query: {
        enabled: !!productIdForReport && productIdForReport !== '',
      },
    },
  );

  const defaultComment = useMemo(() => {
    if (!productsData || isProductLoading || !productIdForReport) {
      return '';
    }
    const product = productsData[0];
    const link = `/a/${product.link}`;
    return t('product_complaint_text', { productId: String(productIdForReport), productLink: link });
  }, [productsData, isProductLoading, t, productIdForReport]);

  const { mutateAsync: sendSupportRequest } = useTelegramSupportContactsCreate();

  const onSubmit = useCallback(
    async (values: FormValues) => {
      setIsLoading(true);
      try {
        await sendSupportRequest({
          data: {
            subjectId: values.type,
            name: values.name,
            email: values.email,
            message: values.comment,
            phoneNumber: values.phone,
            recaptchaResponse: values.token,
          },
        });

        open(
          'after-support-modal',
          <AfterSupportModal
            onOk={() => {
              close('after-support-modal');
            }}
          />,
          true,
          'md:h-unset h-unset absolute md:static bottom-0 rounded-b-none md:rounded-b-[24px] m-0',
        );
      } catch {
        toast.error(t('submit_error'));
      } finally {
        setIsLoading(false);
      }
    },
    [sendSupportRequest, close, open, t],
  );

  return (
    <div className='mx-auto' data-product-report-id={productIdForReport ?? undefined}>
      {isUserLoading || isLoading || isProductLoading ? (
        <Skeleton className='max-w-2xl w-full h-[500px]' />
      ) : (
        <>
          <SupportForm
            key={productIdForReport ?? 'default'}
            defaultComment={defaultComment}
            onSubmit={onSubmit}
            {...pr}
          />
          <div className='w-full mx-auto bg-white rounded-[12px] lg:shadow-[0_3px_12px_0_rgba(40,65,155,0.12)] p-[24px] flex flex-col gap-[24px] mt-[16px]'>
            <h3 className='text-[18px] font-semibold'>{t('additional_contacts')}</h3>

            <div className='text-[16px] font-normal'>
              {t('additional_contacts_email_text')}
              <a href='mailto:info@doska.tv' className='text-primary-green-day underline underline-offset-2'>
                {t('additional_contacts_email_link_text')}
              </a>
            </div>

            <div className='text-[16px] font-normal flex flex-wrap items-center gap-[5px]'>
              {t.rich('additional_contacts_phone_text', {
                whatsapp: (chunks) => (
                  <a
                    href={whatsappHref}
                    target='_blank'
                    rel='noreferrer'
                    className='inline-flex items-center gap-[6px] text-primary-green-day underline underline-offset-2'
                  >
                    <Image src='/assets/icons/whatsapp.svg' width={18} height={18} alt='' />
                    <span>{chunks}</span>
                  </a>
                ),
                phone: (chunks) => (
                  <a
                    href={contactPhoneHref}
                    dir='ltr'
                    className='text-primary-green-day mt-[3px] underline underline-offset-2'
                  >
                    {chunks}
                  </a>
                ),
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
